import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline';

interface FieldComparison {
  fieldPath: string;
  fieldLabel: string;
  previousValue: any;
  currentValue: any;
  hasChanged: boolean;
}

interface VersionComparisonProps {
  previousVersion: any;
  currentVersion: any;
  fields: {
    path: string;
    label: string;
    type: 'text' | 'number' | 'currency' | 'boolean' | 'date';
  }[];
  onUpdateField: (fieldPath: string, value: any) => void;
  sectionTitle: string;
}

export default function VersionComparison({
  previousVersion,
  currentVersion,
  fields,
  onUpdateField,
  sectionTitle
}: VersionComparisonProps) {
  const [comparisons, setComparisons] = useState<FieldComparison[]>([]);
  const [skipToNew, setSkipToNew] = useState(false);

  useEffect(() => {
    generateComparisons();
  }, [previousVersion, currentVersion, fields]);

  const generateComparisons = () => {
    const fieldComparisons = fields.map(field => {
      const previousValue = getNestedValue(previousVersion, field.path);
      const currentValue = getNestedValue(currentVersion, field.path);
      
      return {
        fieldPath: field.path,
        fieldLabel: field.label,
        previousValue,
        currentValue: currentValue ?? previousValue,
        hasChanged: previousValue !== currentValue && currentValue !== undefined
      };
    });

    setComparisons(fieldComparisons);
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((curr, key) => curr?.[key], obj);
  };

  const formatValue = (value: any, type: string): string => {
    if (value === null || value === undefined) return '-';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-CA', {
          style: 'currency',
          currency: 'CAD'
        }).format(value);
      case 'number':
        return new Intl.NumberFormat('en-CA').format(value);
      case 'date':
        return format(new Date(value), 'MMM dd, yyyy');
      case 'boolean':
        return value ? 'Yes' : 'No';
      default:
        return String(value);
    }
  };

  const handleFieldUpdate = (fieldPath: string, value: any, keepPrevious: boolean) => {
    if (keepPrevious) {
      // Keep the previous value
      onUpdateField(fieldPath, comparisons.find(c => c.fieldPath === fieldPath)?.previousValue);
    } else {
      // Update to new value
      onUpdateField(fieldPath, value);
    }
  };

  const getFieldType = (fieldPath: string): string => {
    return fields.find(f => f.path === fieldPath)?.type || 'text';
  };

  if (!previousVersion || skipToNew) {
    // Show regular form without comparison
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{sectionTitle}</h3>
          {previousVersion && (
            <button
              onClick={() => setSkipToNew(false)}
              className="text-sm text-amber-600 hover:text-amber-700"
            >
              Show comparison
            </button>
          )}
        </div>
        {fields.map(field => (
          <div key={field.path}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              type={field.type === 'currency' || field.type === 'number' ? 'number' : 'text'}
              value={getNestedValue(currentVersion, field.path) || ''}
              onChange={(e) => onUpdateField(field.path, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
            />
          </div>
        ))}
      </div>
    );
  }

  // Show side-by-side comparison
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {sectionTitle}
          <span className="text-sm font-normal text-gray-500 ml-2">
            Comparing with {format(new Date(previousVersion.created_at), 'MMM yyyy')}
          </span>
        </h3>
        <button
          onClick={() => setSkipToNew(true)}
          className="text-sm text-amber-600 hover:text-amber-700"
        >
          Skip to new entry
        </button>
      </div>

      <div className="bg-gray-50 rounded-lg overflow-hidden">
        <div className="grid grid-cols-2 gap-0 text-sm font-medium text-gray-700 bg-gray-100 p-3">
          <div>PREVIOUS ({format(new Date(previousVersion.created_at), 'MMM dd, yyyy')})</div>
          <div>CURRENT ({format(new Date(), 'MMM dd, yyyy')})</div>
        </div>

        {comparisons.map((comparison, index) => {
          const fieldType = getFieldType(comparison.fieldPath);
          const isUpdated = comparison.currentValue !== comparison.previousValue;
          
          return (
            <div
              key={comparison.fieldPath}
              className={`grid grid-cols-2 gap-4 p-4 border-t border-gray-200 ${
                index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              }`}
            >
              {/* Previous Value */}
              <div className="space-y-2">
                <div className="font-medium text-gray-900">
                  {comparison.fieldLabel}
                </div>
                <div className="text-gray-700">
                  {formatValue(comparison.previousValue, fieldType)}
                </div>
              </div>

              {/* Current Value */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id={`${comparison.fieldPath}_keep`}
                    name={comparison.fieldPath}
                    checked={!isUpdated}
                    onChange={() => handleFieldUpdate(comparison.fieldPath, null, true)}
                    className="text-amber-500 focus:ring-amber-500"
                  />
                  <label
                    htmlFor={`${comparison.fieldPath}_keep`}
                    className="flex items-center cursor-pointer"
                  >
                    <span className="text-sm text-gray-600 mr-2">Still</span>
                    <span className={isUpdated ? 'text-gray-500' : 'font-medium text-gray-900'}>
                      {formatValue(comparison.previousValue, fieldType)}
                    </span>
                    {!isUpdated && <CheckIcon className="h-4 w-4 ml-1 text-green-500" />}
                  </label>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id={`${comparison.fieldPath}_update`}
                    name={comparison.fieldPath}
                    checked={isUpdated}
                    onChange={() => handleFieldUpdate(comparison.fieldPath, comparison.currentValue, false)}
                    className="text-amber-500 focus:ring-amber-500"
                  />
                  <label
                    htmlFor={`${comparison.fieldPath}_update`}
                    className="flex items-center flex-grow cursor-pointer"
                  >
                    <span className="text-sm text-gray-600 mr-2">Update to:</span>
                    <input
                      type={fieldType === 'currency' || fieldType === 'number' ? 'number' : 'text'}
                      value={comparison.currentValue || ''}
                      onChange={(e) => onUpdateField(comparison.fieldPath, e.target.value)}
                      onClick={() => handleFieldUpdate(comparison.fieldPath, comparison.currentValue, false)}
                      className={`flex-grow px-2 py-1 border rounded text-gray-900 ${
                        isUpdated
                          ? 'border-amber-500 bg-amber-50 font-medium'
                          : 'border-gray-300 bg-white'
                      }`}
                      placeholder={`Enter new ${comparison.fieldLabel.toLowerCase()}`}
                    />
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Change Summary */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start">
          <ArrowRightIcon className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">
              Changes in this section:
            </p>
            <ul className="text-sm text-amber-800 mt-1">
              {comparisons.filter(c => c.hasChanged).map(c => (
                <li key={c.fieldPath}>
                  • {c.fieldLabel}: {formatValue(c.previousValue, getFieldType(c.fieldPath))} → {formatValue(c.currentValue, getFieldType(c.fieldPath))}
                </li>
              ))}
              {comparisons.filter(c => c.hasChanged).length === 0 && (
                <li>No changes in this section</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}